'use client'

import React, { Suspense, useEffect, useRef } from 'react'

import { useQuery } from '@tanstack/react-query'

import {
  useRecipientAnalyticsQueries,
  useSuspenseRecipientDashboard,
} from '@/hooks/usaspending'

import type {
  GeographicDistribution,
  RecipientDashboard,
} from '@/types/usaspending'

import { RecipientGeographyPanel } from '../../charts/RecipientGeographyPanel'
import { QueryErrorBoundary } from '../../DataErrorBoundary'
import { SectionCard, SectionSkeleton, useDetailFilter } from '../../shared'
import { MapUnavailable } from '../../shared/ServiceUnavailable'
import { useRecipientDetail } from '../context'

/**
 * RecipientDetail.Geography
 *
 * Bubble map showing place-of-performance spending across the US.
 * Full-width section with a richer map, metric toggle, and ranked state list.
 */
export function GeographySection() {
  const { recipientId } = useRecipientDetail()

  return (
    <SectionCard
      id="geography"
      title="Geographic Footprint"
      subtitle="Place of Performance"
      fullWidth
      animationDelay={200}
    >
      <QueryErrorBoundary
        title="Failed to load geographic data"
        serviceUnavailableFallback={<MapUnavailable height={360} />}
      >
        <Suspense fallback={<SectionSkeleton height={360} bars={0} />}>
          <GeographyContent id={recipientId} />
        </Suspense>
      </QueryErrorBoundary>
    </SectionCard>
  )
}

function GeographyContent({ id }: { id: string }) {
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  const { data: dashboard } = useSuspenseRecipientDashboard(id, fiscalYear)

  if (dashboard.geography === null) {
    return <DeferredGeography id={id} fiscalYear={fiscalYear} />
  }

  return <GeographyInner data={dashboard.geography} dashboard={dashboard} />
}

function DeferredGeography({
  id,
  fiscalYear,
}: {
  id: string
  fiscalYear?: number
}) {
  const queries = useRecipientAnalyticsQueries()
  const { data: dashboard } = useSuspenseRecipientDashboard(id, fiscalYear)
  const { data, isLoading } = useQuery(queries.geography(id, fiscalYear))

  if (isLoading) return <SectionSkeleton height={360} bars={0} />
  if (!data) return null

  return <GeographyInner data={data} dashboard={dashboard} />
}

function GeographyInner({
  data,
  dashboard,
}: {
  data: GeographicDistribution[]
  dashboard?: RecipientDashboard
}) {
  const chartRef = useRef<HTMLDivElement>(null)
  const { registerChartRef } = useRecipientDetail()

  // Register chart ref for PDF export capture
  useEffect(() => {
    const element = chartRef.current
    if (element) {
      registerChartRef('geography', element)
    }
    return () => {
      registerChartRef('geography', null)
    }
  }, [registerChartRef])

  return (
    <div ref={chartRef}>
      <RecipientGeographyPanel data={data} dashboard={dashboard} height={360} />
    </div>
  )
}
